import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ColDef,
  GridApi,
  GridReadyEvent,
  RowClassRules,
  ValueFormatterParams,
} from 'ag-grid-community';
import { useNavigate } from 'react-router-dom';
import { Search, X, AlertTriangle, Calculator, Landmark } from 'lucide-react';
import { LopAgGrid } from '../../components/LopAgGrid';
import { LopSearchModal } from '../../components/LopSearchModal';
import { LopGridCustomizationDialog } from '../../components/LopGridCustomizationDialog';
import { CalcDetailsModal } from '../../components/CalcDetailsModal';
import { useGridCustomization } from '../../hooks/useGridCustomization';
import { LopGridToolbar, LopGridStatusBar } from '../../components/LopGridToolbar';
import { exportGridCsv } from '../../utils/exportGridCsv';
import { useGridContextMenu } from '../../hooks/useGridContextMenu';
import type { UserCustomizationSettings } from '../../types/UserCustomization';
import {
  applyCustomization,
  buildZebraRule,
  computeSummaryRow,
} from '../../utils/applyCustomization';
import { getHoldings } from '../../api/holdings';
import type { Holding } from '../../types/Holding';
import { LopButton } from '../../components/LopButton';
import { t } from '../../i18n/t';
import './HoldingsPage.css';
import { showErrorToast, describeError } from '../../components/ErrorToast';
import { formatDateOnly, formatDateTimeIL } from '../../utils/dateTime';

// DATETIME2 / DATE ISO string → yyyy-mm-dd (date part only). Read-only display.
// Same pattern as InventoryPage.fmtDate.
function fmtDate(p: ValueFormatterParams<Holding>): string {
  return formatDateOnly(p.value);
}

function fmtDateTime(p: ValueFormatterParams<Holding>): string {
  return formatDateTimeIL(p.value);
}

// rateDate (DATE ISO string) -> dd/MM/yyyy. Empty = the rate is live (no fixed date).
function fmtRateDate(p: ValueFormatterParams<Holding>): string {
  const v = p.value;
  if (!v) return '';
  const s = String(v).slice(0, 10);
  const [y, m, d] = s.split('-');
  return y && m && d ? `${d}/${m}/${y}` : s;
}

// View's rateSource text -> Hebrew label; anything starting with 'TBD' = data not built yet.
function fmtRateSource(p: ValueFormatterParams<Holding>): string {
  const v = p.value as string | null | undefined;
  if (!v) return '';
  if (v.startsWith('TBD')) return t('holdings.rateSources.tbd');
  switch (v) {
    case 'current': return t('holdings.rateSources.current');
    case 'closing': return t('holdings.rateSources.closing');
    case 'special rate': return t('holdings.rateSources.specialRate');
    case 'NAV current': return t('holdings.rateSources.navCurrent');
    case 'NAV closing': return t('holdings.rateSources.navClosing');
    case 'asset not in catalog': return t('holdings.rateSources.notInCatalog');
    default: return v;
  }
}

const HOLDINGS_RESOURCE_CODE = 'holdings';

// Read-only "אחזקות" / Holdings screen — NOT a LopCrudPage (no add/edit/
// delete/save). Manual fetch model: a fund picker drives a DRAFT selection;
// the user explicitly clicks "הבא נתונים" to commit it (active selection)
// and trigger the GET. A stale indicator appears whenever draft ≠ active.
//
// The right-click context menu (Copy / Export / Customize) is wired via the
// shared `useGridContextMenu` hook — same UX as LopCrudPage, no Paste item
// since this screen is read-only.
//
// Layout (RTL):
//   [קרן: ✕ <שם הקרן | "כל הקרנות">  🔍]  [הבא נתונים]  ⚠
//   ─────────────────────────────────────────────────────────
//   <LopAgGrid>
const LENDING_ACCRUAL_BALANCE_TYPE = 2028;

export function HoldingsPage() {
  const navigate = useNavigate();
  const [draftFundId, setDraftFundId] = useState<number | null>(null);
  const [draftFundName, setDraftFundName] = useState<string | null>(null);
  const [activeFundId, setActiveFundId] = useState<number | null>(null);
  const [rows, setRows] = useState<Holding[]>([]);
  // No data is loaded until the first click on "הבא נתונים" (user usually changes the selector first).
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchModalOpen, setSearchModalOpen] = useState(false);

  // Customization dialog state — same shape as LopCrudPage. The dialog itself
  // is owned by this page; the context-menu hook just triggers it via the
  // onCustomize callback.
  const [customizationOpen, setCustomizationOpen] = useState(false);
  const [dialogSampleRow, setDialogSampleRow] = useState<Record<string, unknown> | null>(null);
  const [dialogInitialField, setDialogInitialField] = useState<string | null>(null);
  // Customization incl. live layout, captured when the dialog opens.
  const [dialogCurrent, setDialogCurrent] = useState<UserCustomizationSettings | null>(null);
  // Standard grid toolbar state (search box + row counts for the status bar).
  const [quickFilter, setQuickFilter] = useState('');
  const [displayedRowCount, setDisplayedRowCount] = useState(0);

  // "פירוט חישובים" modal state — opened via the page-specific extraItems
  // entry on the right-click menu. Non-null means open.
  const [calcDetailsRow, setCalcDetailsRow] = useState<Holding | null>(null);
  const openCalcDetails = useCallback((row: Holding) => {
    setCalcDetailsRow(row);
  }, []);
  const closeCalcDetails = useCallback(() => {
    setCalcDetailsRow(null);
  }, []);

  const gridApiRef = useRef<GridApi<Holding> | null>(null);

  // Explicit-save customization model (v0.1.84) — see useGridCustomization.
  const {
    customization,
    dirty: customizationDirty,
    toastVisible,
    onGridReady: onCustomizationGridReady,
    onColumnMoved,
    onColumnResized,
    onSortChanged,
    snapshot: snapshotCustomization,
    applyDraft: applyCustomizationDraft,
    quickCustomize,
    reset: resetCustomization,
    saveCustomizationNow,
  } = useGridCustomization(HOLDINGS_RESOURCE_CODE, gridApiRef);

  // Filter version — bumped on AgGrid model updates so the pinned-bottom
  // summary recomputes against the latest filter/sort state (Community-tier
  // stand-in; same pattern as LopCrudPage).
  const [filterVersion, setFilterVersion] = useState(0);

  // Stable fetch: avoid stale-closure issues when the auto-refresh interval
  // fires after activeFundId changes. The interval reads through this ref.
  const activeFundIdRef = useRef<number | null>(activeFundId);
  useEffect(() => {
    activeFundIdRef.current = activeFundId;
  }, [activeFundId]);

  // Initial load (all rows) + refetch whenever the ACTIVE filter changes.
  // Draft selection alone does NOT trigger a fetch — that's the whole point
  // of the manual "הבא נתונים" model.
  useEffect(() => {
    if (!fetched) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getHoldings(activeFundId ?? undefined)
      .then((data) => {
        if (cancelled) return;
        setRows(data);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        showErrorToast(e, { title: t('errorToast.titleLoad') });
        setError(t('holdings.errorPrefix', { message: describeError(e).body }));
        setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeFundId, fetched]);

  const awaitingHint = useMemo(
    () =>
      `<span class="lop-crud-page__await-hint">${t('common.awaitingFetchHint', {
        fetch: t('holdings.fetch'),
      })}</span>`,
    [],
  );

  const isStale = fetched && draftFundId !== activeFundId;

  // Auto-refresh — paused while stale (draftFundId !== activeFundId). Banking
  // safety: never wipe an in-progress decision with a silent reload.
  useEffect(() => {
    const seconds = customization?.grid.autoRefreshSeconds ?? 0;
    if (!seconds || seconds <= 0) return;
    if (!fetched || isStale) return;
    const intervalMs = seconds * 1000;
    const id = setInterval(() => {
      void getHoldings(activeFundIdRef.current ?? undefined).then((data) => {
        setRows(data);
      });
    }, intervalMs);
    return () => clearInterval(id);
  }, [customization, isStale, fetched]);

  const handleClearDraft = useCallback(() => {
    setDraftFundId(null);
    setDraftFundName(null);
  }, []);

  const handleFetch = useCallback(() => {
    setActiveFundId(draftFundId);
    setFetched(true);
  }, [draftFundId]);

  // LopSearchModal column descriptors — memoized to keep modal props stable.
  const searchColumns = useMemo(
    () => [
      { field: 'fundId', headerName: t('funds.columnFundId') },
      { field: 'fundName', headerName: t('funds.columnFundName') },
      { field: 'fundOfficialName', headerName: t('funds.columnFundOfficialName') },
      { field: 'fundAssetId', headerName: t('funds.columnFundAssetId') },
    ],
    [],
  );

  const handleModalSelect = useCallback((row: Record<string, unknown>) => {
    setDraftFundId(Number(row.fundId));
    setDraftFundName(String(row.fundName ?? ''));
    setSearchModalOpen(false);
  }, []);

  const handleModalClose = useCallback(() => {
    setSearchModalOpen(false);
  }, []);

  // ColumnDefs — memoized (LopAgGrid prop-identity rule). Order matches the
  // spec: fund identity / account / asset / quantities / rates / codes /
  // dates / misc / audit. All read-only.
  const columnDefs = useMemo<ColDef<Holding>[]>(
    () => [
      // 1) fund identity
      { field: 'fundAssetId', headerName: t('holdings.columns.fundAssetId'), width: 120, cellDataType: 'number', editable: false },
      { field: 'fundId', headerName: t('holdings.columns.fundId'), width: 100, cellDataType: 'number', editable: false },
      { field: 'fundName', headerName: t('holdings.columns.fundName'), width: 200, editable: false },
      // 2) account
      { field: 'acc', headerName: t('holdings.columns.acc'), width: 130, editable: false },
      { field: 'bankId', headerName: t('holdings.columns.bankId'), width: 90, cellDataType: 'number', editable: false },
      { field: 'branch', headerName: t('holdings.columns.branch'), width: 90, cellDataType: 'number', editable: false },
      // 3) asset
      { field: 'assetId', headerName: t('holdings.columns.assetId'), width: 120, cellDataType: 'number', editable: false },
      { field: 'assetName', headerName: t('holdings.columns.assetName'), width: 200, editable: false },
      { field: 'symbol', headerName: t('holdings.columns.symbol'), width: 110, editable: false },
      { field: 'assetTypeId', headerName: t('holdings.columns.assetTypeId'), width: 110, cellDataType: 'number', editable: false },
      // 4) quantities
      { field: 'qty', headerName: t('holdings.columns.qty'), width: 130, cellDataType: 'number', editable: false },
      { field: 'qty4Receive', headerName: t('holdings.columns.qty4Receive'), width: 120, cellDataType: 'number', editable: false },
      // 5) rates
      { field: 'rate4Valuation', headerName: t('holdings.columns.rate4Valuation'), width: 120, cellDataType: 'number', editable: false },
      { field: 'crcyRate4Valuation', headerName: t('holdings.columns.crcyRate4Valuation'), width: 140, cellDataType: 'number', editable: false },
      { field: 'indexRate4Valuation', headerName: t('holdings.columns.indexRate4Valuation'), width: 130, cellDataType: 'number', editable: false },
      { field: 'indexRatio4Valuation', headerName: t('holdings.columns.indexRatio4Valuation'), width: 130, cellDataType: 'number', editable: false },
      { field: 'forwardExecutionRate', headerName: t('holdings.columns.forwardExecutionRate'), width: 150, cellDataType: 'number', editable: false },
      { field: 'rateSource', headerName: t('holdings.columns.rateSource'), width: 140, editable: false, valueFormatter: fmtRateSource },
      { field: 'rateDate', headerName: t('holdings.columns.rateDate'), width: 120, editable: false, valueFormatter: fmtRateDate },
      { field: 'manualRateComment', headerName: t('holdings.columns.manualRateComment'), width: 180, editable: false },
      // 6) codes
      { field: 'balanceTypeId', headerName: t('holdings.columns.balanceTypeId'), width: 110, cellDataType: 'number', editable: false },
      { field: 'inventorySourceId', headerName: t('holdings.columns.inventorySourceId'), width: 100, cellDataType: 'number', editable: false },
      { field: 'priceTypeId', headerName: t('holdings.columns.priceTypeId'), width: 100, cellDataType: 'number', editable: false },
      // 7) dates
      { field: 'executionDate', headerName: t('holdings.columns.executionDate'), width: 120, editable: false, valueFormatter: fmtDate },
      { field: 'expirationDate', headerName: t('holdings.columns.expirationDate'), width: 120, editable: false, valueFormatter: fmtDate },
      { field: 'expDate', headerName: t('holdings.columns.expDate'), width: 120, editable: false, valueFormatter: fmtDate },
      // 8) misc
      { field: 'remark', headerName: t('holdings.columns.remark'), width: 160, editable: false },
      { field: 'crcyId', headerName: t('holdings.columns.crcyId'), width: 90, cellDataType: 'number', editable: false },
      { field: 'idxId', headerName: t('holdings.columns.idxId'), width: 90, cellDataType: 'number', editable: false },
      // 9) audit
      { field: 'createdAt', headerName: t('holdings.columns.createdAt'), width: 150, editable: false, valueFormatter: fmtDateTime },
      { field: 'updatedAt', headerName: t('holdings.columns.updatedAt'), width: 150, editable: false, valueFormatter: fmtDateTime },
      // 10) V1.27.x — computed columns (read-only, end of grid)
      { field: 'calcMethodCode', headerName: t('holdings.columns.calcMethodCode'), width: 110, editable: false },
      { field: 'multiplier', headerName: t('holdings.columns.multiplier'), width: 100, cellDataType: 'number', editable: false },
      { field: 'valuation', headerName: t('holdings.columns.valuation'), width: 140, cellDataType: 'number', editable: false },
    ],
    [],
  );

  // Apply customization on top of the base columnDefs (reorder + styling).
  const effectiveColumnDefs = useMemo<ColDef<Holding>[]>(
    () => applyCustomization(columnDefs, customization),
    [columnDefs, customization],
  );

  // Pinned bottom summary row — only when at least one numeric column has
  // a summary configured. Reads filtered+sorted rows from AgGrid so it
  // reflects what the user currently sees.
  const pinnedBottomRowData = useMemo<Record<string, unknown>[] | undefined>(() => {
    const api = gridApiRef.current;
    if (!api) return undefined;
    const visibleRows: Holding[] = [];
    api.forEachNodeAfterFilterAndSort((node) => {
      if (node.data) visibleRows.push(node.data);
    });
    const summary = computeSummaryRow(visibleRows, customization, columnDefs);
    return summary ? [summary] : undefined;
    // filterVersion is the trigger; rows is included for safety.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterVersion, customization, columnDefs, rows]);

  // Zebra-striping rule (only when enabled in customization).
  const rowClassRules = useMemo<RowClassRules<Holding> | undefined>(() => {
    const zebra = buildZebraRule(customization);
    if (!zebra) return undefined;
    return zebra as RowClassRules<Holding>;
  }, [customization]);

  const handleCustomize = useCallback(
    (sampleRow: Record<string, unknown> | null, colId: string | null) => {
      setDialogCurrent(snapshotCustomization());
      setDialogSampleRow(sampleRow);
      setDialogInitialField(colId);
      setCustomizationOpen(true);
    },
    [snapshotCustomization],
  );

  // "פירוט חישובים" extra menu item — memoized so the menuItems memo in
  // useGridContextMenu keeps a stable identity per render. Only shown when
  // the user right-clicked an actual row (not an empty cell).
  const extraMenuItems = useCallback(
    ({ rowData }: { rowData: Record<string, unknown> | null }) => {
      if (!rowData) return [];
      const row = rowData as unknown as Holding;
      const items = [
        {
          label: t('holdings.contextMenu.calcDetails'),
          icon: <Calculator size={16} />,
          onClick: () => openCalcDetails(row),
        },
      ];
      // 2028 = 'זחילת עמלת השאלה': drill down to the Lendings screen on that account.
      if (row.balanceTypeId === LENDING_ACCRUAL_BALANCE_TYPE && row.bankId != null) {
        items.push({
          label: t('holdings.contextMenu.lendingFeeDetails'),
          icon: <Landmark size={16} />,
          onClick: () =>
            navigate(`/lendings?bankId=${row.bankId}&acc=${encodeURIComponent(row.acc)}`),
        });
      }
      return items;
    },
    [openCalcDetails, navigate],
  );

  // Read-only screen → no onPaste. The hook simply omits the Paste menu item.
  // Right-click "עיצוב מהיר": persisted per-column shortcut.
  const getColumnCustomization = useCallback(
    (colId: string) => customization?.columns[colId],
    [customization],
  );
  const handleQuickCustomize = quickCustomize;

  const { onCellContextMenu, contextMenu, getRowStyle, clearRowColors } = useGridContextMenu<Holding>({
    gridApiRef,
    columnDefs,
    resourceCode: HOLDINGS_RESOURCE_CODE,
    titleKey: 'holdings.pageTitle',
    onCustomize: handleCustomize,
    onQuickCustomize: handleQuickCustomize,
    getColumnCustomization,
    extraItems: extraMenuItems,
  });

  // Manual row colors are ephemeral: wipe them whenever the data is reloaded
  // (initial load, filter change, auto-refresh — every path goes via setRows).
  useEffect(() => {
    clearRowColors();
  }, [rows, clearRowColors]);

  const handleGridReady = useCallback(
    (event: GridReadyEvent<Holding>) => {
      gridApiRef.current = event.api;
      onCustomizationGridReady(event);
    },
    [onCustomizationGridReady],
  );

  // Bump the filter version on every model update so pinned-bottom recomputes
  // against the latest filter/sort state.
  const handleModelUpdated = useCallback(() => {
    setFilterVersion((v) => v + 1);
    if (gridApiRef.current) setDisplayedRowCount(gridApiRef.current.getDisplayedRowCount());
  }, []);

  const handleExportCsv = useCallback(() => {
    exportGridCsv(gridApiRef.current, HOLDINGS_RESOURCE_CODE);
  }, []);

  const handleSaveCustomization = useCallback(() => {
    return saveCustomizationNow();
  }, [saveCustomizationNow]);

  const handleDialogClose = useCallback(() => {
    setCustomizationOpen(false);
    setDialogCurrent(null);
    setDialogSampleRow(null);
    setDialogInitialField(null);
  }, []);

  // Stable getRowId — must NOT be inlined (LopAgGrid prop-identity rule).
  const getRowId = useCallback(
    (p: { data: Holding }) => String(p.data.inventoryId),
    [],
  );

  return (
    <div className="holdings-page">
      <h1 className="holdings-page__title">{t('holdings.pageTitle')}</h1>

      <div className="holdings-page__filter-bar" dir="rtl">
        <label className="holdings-page__filter-label">
          {t('holdings.fundLabel')}
        </label>

        {draftFundId != null && (
          <button
            type="button"
            className="holdings-page__icon-btn"
            onClick={handleClearDraft}
            aria-label={t('holdings.clearFund')}
            title={t('holdings.clearFund')}
          >
            <X size={16} aria-hidden="true" />
          </button>
        )}

        <span
          className={
            draftFundName
              ? 'holdings-page__fund-display'
              : 'holdings-page__fund-display holdings-page__fund-display--muted'
          }
        >
          {draftFundName ?? t('holdings.allFunds')}
        </span>

        <button
          type="button"
          className="holdings-page__icon-btn"
          onClick={() => setSearchModalOpen(true)}
          aria-label={t('holdings.selectFund')}
          title={t('holdings.selectFund')}
        >
          <Search size={16} aria-hidden="true" />
        </button>

        <LopButton variant="primary" loading={loading} onClick={handleFetch}>
          {t('holdings.fetch')}
        </LopButton>

        {isStale && (
          <span
            className="holdings-page__stale"
            title={t('holdings.staleIndicator')}
            aria-label={t('holdings.staleIndicator')}
          >
            <AlertTriangle size={18} aria-hidden="true" />
          </span>
        )}

        <div className="holdings-page__toolbar">
          {/* Standard grid toolbar (same as LopCrudPage). Read-only screen:
              the edit buttons are shown but disabled. */}
          <LopGridToolbar
            quickFilter={quickFilter}
            onQuickFilterChange={setQuickFilter}
            readOnly
            exportDisabled={!fetched}
            onExport={handleExportCsv}
          />
        </div>
      </div>

      {error && <div className="holdings-page__error">{error}</div>}

      <div className="holdings-page__grid">
        <LopAgGrid<Holding>
          rowData={rows}
          columnDefs={effectiveColumnDefs}
          getRowId={getRowId}
          loading={loading}
          rowClassRules={rowClassRules}
          getRowStyle={getRowStyle}
          pinnedBottomRowData={pinnedBottomRowData}
          onGridReady={handleGridReady}
          onCellContextMenu={onCellContextMenu}
          onModelUpdated={handleModelUpdated}
          onColumnMoved={onColumnMoved}
          onColumnResized={onColumnResized}
          onSortChanged={onSortChanged}
          quickFilterText={quickFilter}
          // Suppress the browser's native right-click menu so our custom menu
          // isn't immediately overwritten by it. (Same rule as LopCrudPage.)
          preventDefaultOnContextMenu={true}
          overlayNoRowsTemplate={fetched ? undefined : awaitingHint}
        />
      </div>

      <LopGridStatusBar
        totalCount={rows.length}
        displayedCount={displayedRowCount}
        isFiltered={quickFilter.length > 0 && displayedRowCount < rows.length}
        customizationDirty={customizationDirty}
        onSaveCustomization={handleSaveCustomization}
        customizationToastVisible={toastVisible}
      />

      {searchModalOpen && (
        <LopSearchModal
          title={t('holdings.selectFund')}
          table="MutualFunds"
          columns={searchColumns}
          onSelect={handleModalSelect}
          onClose={handleModalClose}
        />
      )}

      {contextMenu}

      {customizationOpen && (
        <LopGridCustomizationDialog
          resourceCode={HOLDINGS_RESOURCE_CODE}
          baseColumnDefs={columnDefs as ColDef<any>[]}
          current={dialogCurrent}
          sampleRowData={dialogSampleRow}
          initialSelectedField={dialogInitialField}
          onSave={applyCustomizationDraft}
          onReset={resetCustomization}
          onClose={handleDialogClose}
        />
      )}

      {calcDetailsRow != null && (
        <CalcDetailsModal row={calcDetailsRow} onClose={closeCalcDetails} />
      )}
    </div>
  );
}
